import { CheckCircle2, MessageCircle, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";

const publicSiteUrl = import.meta.env.DEV
  ? "http://localhost:3000"
  : "https://tribunalea.com";

function accountTypeLabel(type) {
  return type === "institutional" ? "institucional" : "personal";
}

export function WhatsappSettingsPage({ api }) {
  const [status, setStatus] = useState(null);
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [verification, setVerification] = useState(null);
  const [code, setCode] = useState("");
  const [transfer, setTransfer] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const next = await api.whatsappLink();
      setStatus(next);
      if (next.link?.phoneE164) setPhone(next.link.phoneE164);
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const requestCode = async (confirmTransfer = false) => {
    setSubmitting(true);
    setError("");
    try {
      const response = await api.requestWhatsappVerification({
        phoneE164: phone,
        consent,
        confirmTransfer,
      });
      if (response.requiresTransferConfirmation) {
        setTransfer(response);
        return;
      }
      setTransfer(null);
      setVerification(response);
      setCode("");
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSubmitting(false);
    }
  };

  const confirmCode = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const confirmed = await api.confirmWhatsappVerification({
        verificationId: verification.verificationId,
        code,
      });
      setResult(confirmed);
      setStatus(confirmed);
      setVerification(null);
      setTransfer(null);
    } catch (confirmError) {
      setError(confirmError.message);
    } finally {
      setSubmitting(false);
    }
  };

  const unlink = async () => {
    if (!window.confirm("¿Desvincular este número de WhatsApp?")) return;
    setSubmitting(true);
    setError("");
    try {
      await api.unlinkWhatsapp();
      setStatus({ linked: false, link: null });
      setResult(null);
      setVerification(null);
      setPhone("");
      setConsent(false);
    } catch (unlinkError) {
      setError(unlinkError.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page whatsapp-settings-page">
      <div className="whatsapp-settings-content">
        <header className="whatsapp-settings-heading">
          <span>CUENTA · WHATSAPP</span>
          <h1>Justiniano en WhatsApp</h1>
          <p>
            Vinculá tu número para conversar con el mismo Justiniano de Civita.
            Las respuestas usan tu historial, las mismas fuentes y los créditos de
            esta cuenta.
          </p>
        </header>

        <section className="whatsapp-settings-notice">
          <ShieldCheck size={21} aria-hidden="true" />
          <div>
            <strong>Tu cuenta queda protegida por verificación</strong>
            <p>
              Te enviaremos un código desde el número oficial de Tribunalea. Un
              número sólo puede estar vinculado a una cuenta a la vez.
            </p>
          </div>
        </section>

        {error ? <div className="whatsapp-settings-error" role="alert">{error}</div> : null}

        {loading ? (
          <div className="whatsapp-settings-card" role="status">Cargando vinculación…</div>
        ) : status?.linked ? (
          <section className="whatsapp-settings-card whatsapp-settings-linked">
            <CheckCircle2 size={28} aria-hidden="true" />
            <div>
              <span>Número vinculado</span>
              <strong>{status.link.phoneE164}</strong>
              <small>Cuenta {accountTypeLabel(status.link.accountType)}</small>
            </div>
            <button type="button" className="secondary" disabled={submitting} onClick={unlink}>
              Desvincular
            </button>
            {result?.replacedAccount ? (
              <p className="whatsapp-settings-result">
                La vinculación anterior con {result.replacedAccount.email} (
                {accountTypeLabel(result.replacedAccount.accountType)}) fue reemplazada.
              </p>
            ) : null}
          </section>
        ) : verification ? (
          <form className="whatsapp-settings-card whatsapp-settings-form" onSubmit={confirmCode}>
            <MessageCircle size={28} aria-hidden="true" />
            <div>
              <h2>Ingresá el código</h2>
              <p>Lo enviamos por WhatsApp a {verification.phoneE164}.</p>
              {verification.developmentCode ? (
                <p className="whatsapp-settings-dev-code">
                  Código local: <strong>{verification.developmentCode}</strong>
                </p>
              ) : null}
              <label>
                Código de 6 dígitos
                <input
                  value={code}
                  onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  required
                />
              </label>
              <div className="whatsapp-settings-actions">
                <button type="button" className="secondary" onClick={() => setVerification(null)}>
                  Volver
                </button>
                <button type="submit" disabled={submitting || code.length !== 6}>
                  {submitting ? "Verificando…" : "Confirmar y vincular"}
                </button>
              </div>
            </div>
          </form>
        ) : (
          <section className="whatsapp-settings-card whatsapp-settings-form">
            <MessageCircle size={28} aria-hidden="true" />
            <div>
              <h2>Vinculá tu número</h2>
              <p>
                Podés escribirlo en formato internacional o con código de área
                y número.
              </p>
              <label>
                Número de WhatsApp
                <input
                  type="tel"
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                  placeholder="11 1234 5678"
                  autoComplete="tel"
                />
              </label>
              <p className="whatsapp-settings-policy-links">
                Conocé cómo tratamos tus mensajes y protegemos tu cuenta en
                nuestra{" "}
                <a
                  href={`${publicSiteUrl}/privacidad`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Política de Privacidad
                </a>{" "}
                y{" "}
                <a
                  href={`${publicSiteUrl}/seguridad`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Política de Seguridad
                </a>.
              </p>
              <label className="whatsapp-settings-consent">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(event) => setConsent(event.target.checked)}
                />
                <span>
                  Acepto recibir por WhatsApp el código de verificación y mensajes
                  de Justiniano relacionados con mis consultas.
                </span>
              </label>

              {transfer ? (
                <div className="whatsapp-settings-warning" role="alert">
                  <strong>Este número ya está vinculado</strong>
                  <p>
                    Actualmente pertenece a una cuenta {accountTypeLabel(transfer.accountType)}.
                    Si continuás y verificás el código, pasará a esta cuenta.
                  </p>
                  <button type="button" disabled={submitting} onClick={() => requestCode(true)}>
                    Continuar y enviar código
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  disabled={submitting || !consent || !phone.trim()}
                  onClick={() => requestCode(false)}
                >
                  {submitting ? "Enviando…" : "Enviar código por WhatsApp"}
                </button>
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
